import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useUpdateInventoryItem,
  getListInventoryItemsQueryKey,
  getGetInventoryItemQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { ArrowUp, ArrowDown, Plus, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

// Phase 55 (RECIPE-SPEC-01, migration 0121):
// Reusable controlled ordered-list editor for inventory_items.preparation_steps.
// Array position IS the step order (mirrors photoUrls semantics on the server).
// No drag-and-drop library exists in this codebase today (confirmed — no
// dnd-kit / react-beautiful-dnd in package.json), so this uses vanilla
// up/down ArrowUp/ArrowDown icon buttons per 55-CONTEXT.md's Claude's Discretion.
// One row per step: "Step N" label + Textarea (500-char cap) + up/down/remove
// controls. "+ Add Step" appends an empty string to the end.

interface Props {
  value: string[] | null | undefined;
  onChange: (next: string[]) => void;
}

const MAX_STEP_LENGTH = 500;

export function PreparationStepsInput({ value, onChange }: Props) {
  const steps = value ?? [];

  function addStep() {
    onChange([...steps, ""]);
  }

  function updateStep(idx: number, next: string) {
    onChange(steps.map((s, i) => (i === idx ? next : s)));
  }

  function removeStep(idx: number) {
    onChange(steps.filter((_, i) => i !== idx));
  }

  function move(idx: number, delta: -1 | 1) {
    const target = idx + delta;
    if (target < 0 || target >= steps.length) return;
    const next = steps.slice();
    const tmp = next[idx]!;
    next[idx] = next[target]!;
    next[target] = tmp;
    onChange(next);
  }

  return (
    <div className="space-y-2">
      {steps.length === 0 ? (
        <div className="rounded-md border border-dashed px-3 py-3 text-xs text-muted-foreground">
          No preparation steps yet. Click &ldquo;+ Add Step&rdquo; below.
        </div>
      ) : (
        <div className="space-y-2">
          {steps.map((s, i) => (
            <div key={i} className="rounded border p-3 bg-muted/20">
              <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-start">
                <div className="text-xs font-semibold text-muted-foreground pt-2 select-none">
                  Step {i + 1}
                </div>
                <Textarea
                  rows={2}
                  maxLength={MAX_STEP_LENGTH}
                  placeholder="e.g. Boil 300 ml of water in a saucepan."
                  value={s}
                  onChange={(e) => updateStep(i, e.target.value)}
                />
                <div className="flex flex-col gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => move(i, -1)}
                    disabled={i === 0}
                    aria-label={`Move step ${i + 1} up`}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => move(i, 1)}
                    disabled={i === steps.length - 1}
                    aria-label={`Move step ${i + 1} down`}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => removeStep(i)}
                    aria-label={`Remove step ${i + 1}`}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <Button type="button" size="sm" variant="outline" onClick={addStep}>
        <Plus className="mr-2 h-3 w-3" />
        Add Step
      </Button>
    </div>
  );
}

// Phase 55 (RECIPE-SPEC-01): edit-mode self-contained section for an existing
// recipe item — mirrors RecipeBuilderSection's own-state / own-save pattern
// (does NOT go through the outer form's onSubmit). Loads editItem.preparationSteps
// into local state, renders <PreparationStepsInput/>, exposes a Save button that
// PATCHes the item directly and invalidates the list + single-item query keys.

interface SectionProps {
  itemId: number;
  itemName: string;
  initialSteps: string[] | null | undefined;
}

export function PreparationStepsSection({ itemId, itemName, initialSteps }: SectionProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [steps, setSteps] = useState<string[]>(initialSteps ?? []);
  const updateMutation = useUpdateInventoryItem();

  // Track whether the local state diverges from the last-saved value so the
  // Save button only enables on real changes. Compare against initialSteps
  // (server-authoritative snapshot at mount).
  const initial = initialSteps ?? [];
  const dirty =
    steps.length !== initial.length ||
    steps.some((s, i) => s !== initial[i]);

  async function handleSave() {
    // Drop empty/whitespace-only rows so saved data is clean. Send [] to clear,
    // non-empty array to replace — the server's undefined/null-or-[]/value idiom
    // takes care of the actual DB write.
    const cleaned = steps.map((s) => s.trim()).filter((s) => s.length > 0);
    try {
      await updateMutation.mutateAsync({ id: itemId, data: { preparationSteps: cleaned } });
      toast({ title: `Preparation steps saved for ${itemName}.` });
      queryClient.invalidateQueries({ queryKey: getListInventoryItemsQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetInventoryItemQueryKey(itemId) });
    } catch {
      toast({ title: "Failed to save preparation steps.", variant: "destructive" });
    }
  }

  return (
    <>
      <Separator className="my-4" />
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Preparation Steps</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Ordered recipe-card steps. Drag using the arrows to reorder.
            </p>
          </div>
          <Button
            type="button"
            size="sm"
            onClick={handleSave}
            disabled={!dirty || updateMutation.isPending}
          >
            {updateMutation.isPending ? "Saving…" : "Save Steps"}
          </Button>
        </div>
        <PreparationStepsInput value={steps} onChange={setSteps} />
      </div>
    </>
  );
}
